'use client';

import { useCallback, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useAuth } from './AuthProvider';
import Confetti from './Confetti';
import { ymdLocal } from '@/lib/leave';
import { birthdayMessage, ownBirthdayMessage, type BirthdayPerson } from '@/lib/birthdays';

const DISMISS_KEY = 'birthday-banner-dismissed';

function readDismissedDay(): string {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(DISMISS_KEY) || '';
  } catch {
    return '';
  }
}

export default function BirthdayBanner() {
  const pathname = usePathname();
  const { user, loading } = useAuth();
  const [people, setPeople] = useState<BirthdayPerson[]>([]);
  const [celebrate, setCelebrate] = useState(false);
  const [dismissedDay, setDismissedDay] = useState<string | null>(null);

  useEffect(() => {
    setDismissedDay(readDismissedDay());
  }, []);

  useEffect(() => {
    if (loading || !user) {
      setPeople([]);
      setCelebrate(false);
      return;
    }
    let cancelled = false;

    fetch('/api/birthdays')
      .then(r => (r.ok ? r.json() : { birthdays: [] }))
      .then(data => { if (!cancelled) setPeople(data.birthdays || []); })
      .catch(() => { if (!cancelled) setPeople([]); });

    // El servidor decide y estampa la celebración: solo el primer acceso a
    // partir del cumpleaños responde celebrate = true, una vez por año.
    fetch('/api/birthdays/celebrate', { method: 'POST' })
      .then(r => (r.ok ? r.json() : { celebrate: false }))
      .then(data => { if (!cancelled && data.celebrate) setCelebrate(true); })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [user, loading]);

  const dismiss = useCallback(() => {
    const day = ymdLocal(new Date());
    setDismissedDay(day);
    try {
      window.localStorage.setItem(DISMISS_KEY, day);
    } catch {
      /* almacenamiento no disponible: el descarte solo dura la sesión */
    }
  }, []);

  const confettiDone = useCallback(() => setCelebrate(false), []);

  if (pathname === '/login') return null;

  const others = people.filter(p => !p.self);
  // El cumpleañero puede recibir el confetti un día posterior a su cumpleaños
  // (si ese día no entró), cuando ya no aparece en la lista de hoy.
  const me: BirthdayPerson | null =
    people.find(p => p.self)
    ?? (celebrate && user ? { id: user.id, name: user.name, email: user.email, self: true } : null);

  const hidden = dismissedDay === ymdLocal(new Date());
  const showBanner = !hidden && (others.length > 0 || me !== null);

  if (!showBanner && !celebrate) return null;

  return (
    <>
      {celebrate && <Confetti onDone={confettiDone} />}
      {showBanner && (
        <div className="marquesina" role="region" aria-label="Cumpleaños">
          {me && (
            <div className="marquesina-item birthday-item birthday-item-own">
              <span className="birthday-icon" aria-hidden="true">🎉</span>
              <span className="marquesina-text">{ownBirthdayMessage(me)}</span>
              <div className="marquesina-actions">
                <button type="button" className="marquesina-btn birthday-btn" onClick={dismiss}>
                  Ocultar por hoy
                </button>
              </div>
            </div>
          )}
          {others.length > 0 && (
            <div className="marquesina-item birthday-item">
              <span className="birthday-icon" aria-hidden="true">🎂</span>
              <span className="marquesina-text">{birthdayMessage(others)}</span>
              <div className="marquesina-actions">
                <button type="button" className="marquesina-btn birthday-btn" onClick={dismiss}>
                  Ocultar por hoy
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
